import * as THREE from 'three';
import { clamp, damp } from '../core/math';
import { $ } from '../platform/dom';
import { camera } from '../render/scene';
import { enterHangarScene, exitHangarScene } from '../render/hangar-scene';
import { META } from '../platform/storage';
import { ensureParts } from '../sim/meta-parts';
import { P } from '../world/player';
import { renderHangar, selSlot, setSelSlot, setSlotHandler } from './hangar-panel';
/* ============================================================
   機庫 · 檢視與生命週期
   ============================================================

   這個檔只管三件事：**開關、鏡頭、輸入**。面板的內容在 `hangar-panel.ts`。

   ## 為什麼鏡頭要重寫（原本的三個錯）

   1. **軌道中心不在機體上。** 舊版把注視點放在 `x = 2.7`（機體右邊的空氣），
      為的是讓機體落在畫面左半。結果拖曳旋轉時機體是繞著一個偏移的圓心跑 ——
      忽遠忽近、忽左忽右，讀起來像機體在漂移而不是在原地轉。
      **正解是視錐偏移**（`setViewOffset`）：相機仍然繞著機體轉，
      只把投影中心往旁邊挪，兩件事因此分開。
   2. **初始角度不可控。** 舊版自轉角度取自 `performance.now()`，
      所以每次打開機庫看到的角度取決於你開機多久了。
      產品畫面的第一印象不能是隨機的。
   3. **勻速自轉。** 那是模型檢視器的語彙，而且看細節時它一直在動。
      改成：開場緩入 → 靜止 → 閒置 7 秒後才極慢自轉，一碰就停。 */

/** 當前檢視值。渲染每幀讀它。 */
export var VIEW = { yaw: -0.62, pitch: 0.10, dist: 8.6, y: 2.25 };
/** 目標值。輸入與焦點改這個，`VIEW` 用 damp 追上去 —— 順便得到拖曳的慣性。 */
var T = { yaw: -0.62, pitch: 0.10, dist: 8.6, y: 2.25 };

var DIST = { min: 4.5, max: 18 }, PITCH = { min: -0.25, max: 1.05 };

/* 每個部位的觀察角度。**切部位時鏡頭要帶到那個部位**——
   否則「換了推進器」與「換了核心」在畫面上是同一件事，
   而 04-MECHA.md §4 的判準是「小視窗裡認不出換了什麼，那個部件就不存在」。
   機體全高約 4.4：腿 0–2.0、軀幹 2.2–3.3、頭 3.7–4.2。

   `turn` 只給「非得從某個方向看才看得到」的部位（右臂的主武器、肩上的掛架）；
   其餘部位保留玩家自己轉到的角度 —— 每切一次就把鏡頭甩回去是很煩的互動。 */
var FOCUS = {
  /* 機體列：整台入鏡。切機體時要看到的是**剪影的差別**（兩台差 15% 身高、
     寬高比 0.92 對 1.06），拉近看細節反而看不出換了什麼。 */
  unit: { y: 2.20, dist: 10.4, pitch: 0.08, yaw: -0.62, turn: true },
  core: { y: 2.30, dist: 8.4, pitch: 0.06, yaw: -0.62, turn: false },
  cockpit: { y: 3.55, dist: 6.2, pitch: 0.03, yaw: -0.42, turn: false },
  thruster: { y: 1.30, dist: 7.8, pitch: 0.15, yaw: -0.72, turn: false },
  main: { y: 2.55, dist: 8.4, pitch: 0.05, yaw: -1.05, turn: true },
  sensor: { y: 3.60, dist: 5.8, pitch: 0.01, yaw: -0.30, turn: false },
  pylon: { y: 3.00, dist: 8.8, pitch: 0.05, yaw: -1.15, turn: true }
};

var idle = 0;          // 沒有互動的秒數，超過 SPIN_AFTER 才開始自轉
var SPIN_AFTER = 7;

/* ---------- 鏡頭 ---------- */

/** 視錐偏移：把機體對準「面板讓出來的那塊空白」的中心，而不是畫面中心。

    量的是實際 DOM 位置，所以 CSS 改欄寬、或換成窄視窗的直式佈局，
    這裡都自動跟著對 —— 版面與鏡頭各寫一份常數遲早會對不上。 */
export function syncHangarView() {
  if (!hangarOpen()) return;
  var cv = document.querySelector('#stage canvas') as any;
  var w = cv ? cv.clientWidth : window.innerWidth;
  var h = cv ? cv.clientHeight : window.innerHeight;
  var nav = box('hNav'), det = box('hDetail'), foot = box('hFoot'), stage = box('hStage');
  var offX = 0, offY = foot.height / 2;

  if (nav.width > w * 0.6) {
    /* 窄視窗：導航變成頂部橫列、詳情變成底部區塊，讓出來的是**上下之間**的一條。
       水平不偏，垂直對到那條的中心。 */
    offY = h / 2 - (nav.height + stage.height / 2);
  } else {
    offX = (det.width - nav.width) / 2;     // 內容往左移，機體落在兩欄中間
  }
  camera.setViewOffset(w, h, offX, offY, w, h);

  /* 中欄比「整個畫面」矮多少，鏡頭就要退多遠 —— 否則直式佈局下
     機體會以整個畫面的高度來構圖，上下都被面板切掉。
     寬版面時 stage 幾乎等於可用高度，係數自然是 1，構圖不受影響。 */
  fitK = stage.height > 80 ? Math.max(1, (h - foot.height) / stage.height) : 1;
}
/** 鏡頭距離的版面補正，見 `syncHangarView`。 */
var fitK = 1;
var ZERO = { width: 0, height: 0 };
function box(id) {
  var el = document.getElementById(id);
  return el ? el.getBoundingClientRect() : ZERO;
}

/** 鎖住預覽角度（造型比對用，`tools/mech-angles.js`）。傳 null 恢復閒置自轉。

    **這個功能原本是死的**：`api.pose()` 設了一個沒有任何地方讀取的變數，
    所以那批標著「正面／側面／背面」的截圖其實是在自轉狀態下拍的，
    角度標籤與畫面對不上。 */
export function setPose(rad: number | null) {
  posed = rad !== null;
  if (rad !== null) { T.yaw = VIEW.yaw = rad; idle = 0; }
}
var posed = false;

/** 每幀由 `game.ts` 呼叫。`dt` 是真實秒數（機庫不吃 timeScale）。 */
export function updateHangarCamera(dt) {
  idle += dt;
  if (idle > SPIN_AFTER && !posed) T.yaw += dt * 0.09;   // 極慢，只為了讓畫面不死

  VIEW.yaw = damp(VIEW.yaw, T.yaw, 6, dt);
  VIEW.pitch = damp(VIEW.pitch, T.pitch, 6, dt);
  VIEW.dist = damp(VIEW.dist, T.dist, 5, dt);
  VIEW.y = damp(VIEW.y, T.y, 5, dt);

  var cy = Math.cos(VIEW.pitch), d = VIEW.dist * fitK;
  camera.position.set(
    Math.sin(VIEW.yaw) * cy * d,
    VIEW.y + Math.sin(VIEW.pitch) * d,
    Math.cos(VIEW.yaw) * cy * d);
  camera.lookAt(0, VIEW.y, 0);                     // 軌道中心 = 機體中軸
}

/* FOCUS 的 y 是照 CHASSIS-0（全高約 4.9）訂的。LANCER-2 矮 15%，
   直接套的話點「駕艙」鏡頭會停在它頭頂上方的空氣。
   **量實際的 rig 而不是查表**：加第三台機體時這裡不用改，而且部件換裝
   （重載腳架把腿加粗加長）也會自動跟著對。 */
var _box = new THREE.Box3();
function mechScale() {
  var rig = P.rig; if (!rig || !rig.root) return 1;
  rig.root.updateMatrixWorld(true);
  _box.setFromObject(rig.root);
  var h = _box.max.y - Math.min(0, _box.min.y);
  return h > 0.5 ? clamp(h / 4.90, 0.6, 1.4) : 1;
}

/** 切部位時把鏡頭帶到該部位。 */
export function focusSlot(id) {
  var f = FOCUS[id]; if (!f) return;
  var k = mechScale();
  /* 只縮 y 不縮 dist：兩台的**寬度**幾乎一樣（4.41 對 4.53），
     距離跟著身高縮的話矮的那台會被拉得太近、切邊。 */
  T.y = f.y * k; T.dist = f.dist; T.pitch = f.pitch;
  if (f.turn) T.yaw = f.yaw;
  idle = 0;
}
setSlotHandler(focusSlot);

/* ---------- 輸入 ---------- */

/** 指標在面板上就不是要轉機體 —— 那裡的拖曳是要捲動與點按鈕的。 */
function onMech(e) {
  var n = e.target as any;
  while (n) {
    if (n.classList && (n.classList.contains('hcol') || n.classList.contains('hfoot'))) return false;
    n = n.parentNode;
  }
  return true;
}

var drag: any = null, pinch = 0;

export function initHangarControls() {
  var el = document.getElementById('ui') || document.body;

  el.addEventListener('pointerdown', function (e: any) {
    if (!hangarOpen() || !onMech(e)) return;
    drag = { x: e.clientX, y: e.clientY }; idle = 0;
  });
  window.addEventListener('pointermove', function (e: any) {
    if (!drag || !hangarOpen()) return;
    T.yaw -= (e.clientX - drag.x) * 0.010;
    T.pitch = clamp(T.pitch + (e.clientY - drag.y) * 0.005, PITCH.min, PITCH.max);
    drag.x = e.clientX; drag.y = e.clientY; idle = 0;
  });
  window.addEventListener('pointerup', function () { drag = null; });

  el.addEventListener('wheel', function (e: any) {
    if (!hangarOpen() || !onMech(e)) return;
    e.preventDefault();
    T.dist = clamp(T.dist + e.deltaY * 0.010, DIST.min, DIST.max); idle = 0;
  }, { passive: false });

  /* 雙指縮放。手機沒有滾輪，而機庫是這個遊戲唯一需要細看的畫面。 */
  el.addEventListener('touchmove', function (e: any) {
    if (!hangarOpen() || e.touches.length !== 2) return;
    var d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY);
    if (pinch) T.dist = clamp(T.dist + (pinch - d) * 0.03, DIST.min, DIST.max);
    pinch = d; idle = 0;
  }, { passive: true });
  el.addEventListener('touchend', function () { pinch = 0; });

  window.addEventListener('resize', syncHangarView);
}

/* ---------- 開關 ---------- */

export function openHangar() {
  /* **所有已解鎖的機體都補一次起手件**，不只當前這台。機體卡片上要顯示
     「持有部件 n / 19」，只補當前那台的話另一台會先顯示 0，切過去才變 6 ——
     看起來像是「切過去才送你六個」，但其實起手件本來就是免費的。 */
  for (var mi = 0; mi < META.mechs.length; mi++) ensureParts(META.mechs[mi]);
  ensureParts();
  setSelSlot('core');
  enterHangarScene();

  /* 開場緩入：從稍遠、稍偏的角度 damp 到定位。
     一個 0.6 秒的推進讓畫面「動起來」，而且終點永遠一樣 —— 可重現。 */
  var f = FOCUS.core;
  T.yaw = f.yaw; T.pitch = f.pitch; T.dist = f.dist; T.y = f.y;
  VIEW.yaw = f.yaw - 0.40; VIEW.pitch = f.pitch + 0.07;
  VIEW.dist = f.dist + 2.6; VIEW.y = f.y;
  idle = 0;

  renderHangar();
  /* 開始畫面要收起來：兩層 `.screen` 疊在一起的話，機體會被兩片遮罩蓋住，
     而看機體正是這個畫面存在的理由。 */
  $('screenStart').classList.remove('show');
  $('screenHangar').classList.add('show');
  document.body.classList.add('hangar-on');
  /* 面板要先完成佈局才量得到寬度 —— 隔一幀再算視錐偏移。 */
  requestAnimationFrame(syncHangarView);
}

export function closeHangar() {
  $('screenHangar').classList.remove('show');
  $('screenStart').classList.add('show');
  document.body.classList.remove('hangar-on');
  camera.clearViewOffset();
  exitHangarScene();
}

export function hangarOpen() {
  var el = $('screenHangar');
  return !!el && el.classList.contains('show');
}

export { selSlot };
